import { MessageSquare, Pencil, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { useTeamSessions } from "@/features/team-sessions/hooks/useTeamSessions";

export function TeamSessionsListPage() {
  const { t } = useTranslation("teamSessions");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const query = useTeamSessions();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        actions={
          <Button onClick={() => navigate("/sessions/new")}>
            <Plus className="h-4 w-4" />
            {t("new")}
          </Button>
        }
      />

      {query.isLoading ? <LoadingState /> : null}
      {query.isError ? <ErrorState error={query.error} /> : null}
      {query.data && query.data.length === 0 ? (
        <EmptyState title={t("title")} description={t("empty")} />
      ) : null}
      {query.data && query.data.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {query.data.map((ts) => (
            <li
              key={ts.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/sessions/${ts.id}/chat`)}
              onKeyDown={(e) => {
                if (e.key === "Enter")
                  navigate(`/sessions/${ts.id}/chat`);
              }}
              className="shadow-card cursor-pointer rounded-2xl border border-line bg-surface p-5 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-hover"
            >
              <h3 className="line-clamp-1 text-base font-semibold text-ink-950">
                {ts.name}
              </h3>
              <p className="line-clamp-2 text-sm text-ink-500">
                {ts.description || t("noDescription")}
              </p>
              <div className="mt-2 flex items-center gap-3 text-xs text-ink-500">
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {t("maxDelegationsBadge", { count: ts.max_delegations })}
                </span>
              </div>
              <div
                className="mt-3 flex flex-wrap gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  size="sm"
                  onClick={() => navigate(`/sessions/${ts.id}/chat`)}
                >
                  <MessageSquare className="h-3 w-3" />
                  {t("actions.openChat")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/sessions/${ts.id}`)}
                >
                  <Pencil className="h-3 w-3" />
                  {tc("actions.edit")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
